/**
 * Hook personalizado para manejar el refresco de datos en el CRM
 * 
 * PROBLEMA ACTUAL:
 * - Los datos solo se cargan al montar el componente (useEffect con [isAuthenticated])
 * - Cuando un usuario crea/edita/elimina, otros usuarios NO ven los cambios
 * - Solo se actualiza si recargan la página o salen y vuelven a entrar
 * 
 * SOLUCIÓN:
 * - Sistema centralizado de refresco con polling opcional
 * - Refetch automático después de operaciones CRUD
 * - Refetch cuando el usuario vuelve a una pantalla o cambia de pestaña
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook para refrescar datos automáticamente
 * 
 * @param {Function} refreshFunction - Función que carga los datos del backend
 * @param {Object} options - Opciones de configuración
 * @param {number} options.pollingInterval - Intervalo de polling en ms (0 = desactivado)
 * @param {boolean} options.refetchOnWindowFocus - Refrescar cuando la ventana recupera el foco
 * @param {boolean} options.refetchOnTabChange - Refrescar cuando cambia de pestaña en el CRM
 * @param {Array} options.dependencies - Dependencias que activan el refresco
 * 
 * @returns {Object} - { refresh, isRefreshing }
 */
export const useDataRefresh = (refreshFunction, options = {}) => {
  const {
    pollingInterval = 0, // Por defecto sin polling
    refetchOnWindowFocus = true,
    refetchOnTabChange = true,
    dependencies = [],
    enabled = true, // Permitir desactivar el hook
  } = options;

  const isRefreshingRef = useRef(false);
  const intervalRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());

  // Función de refresco con protección contra llamadas simultáneas
  const refresh = useCallback(async () => {
    if (isRefreshingRef.current || !enabled) {
      return;
    }

    try {
      isRefreshingRef.current = true;
      lastRefreshRef.current = Date.now();
      await refreshFunction();
    } catch (error) {
      console.error('Error en refresh automático:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshFunction, enabled]);

  // Polling automático
  useEffect(() => {
    if (!enabled || pollingInterval <= 0) {
      return;
    }

    intervalRef.current = setInterval(() => {
      // Solo refrescar si han pasado al menos pollingInterval ms desde el último refresh
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      if (timeSinceLastRefresh >= pollingInterval) {
        refresh();
      }
    }, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [pollingInterval, refresh, enabled]);

  // Refrescar cuando la ventana recupera el foco
  useEffect(() => {
    if (!enabled || !refetchOnWindowFocus) {
      return;
    }

    const handleFocus = () => {
      // Solo refrescar si han pasado al menos 5 segundos desde el último refresh
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      if (timeSinceLastRefresh >= 5000) {
        refresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh, refetchOnWindowFocus, enabled]);

  // Refrescar cuando cambian las dependencias (ej: activeTab)
  useEffect(() => {
    if (!enabled || dependencies.length === 0) {
      return;
    }

    // Solo refrescar si han pasado al menos 2 segundos desde el último refresh
    const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
    if (timeSinceLastRefresh >= 2000) {
      refresh();
    }
    // Nota: dependencies es intencional - solo queremos refrescar cuando cambian las dependencias externas
    // eslint-disable-next-line
  }, dependencies);

  return {
    refresh,
    isRefreshing: isRefreshingRef.current,
  };
};

/**
 * Hook para invalidar y refrescar datos después de operaciones CRUD
 * 
 * @param {Function} refreshFunction - Función que carga los datos del backend
 * @returns {Function} - Función invalidate que debe llamarse después de CRUD
 */
export const useInvalidateData = (refreshFunction) => {
  const invalidate = useCallback(async () => {
    try {
      await refreshFunction();
    } catch (error) {
      console.error('Error invalidando datos:', error);
    }
  }, [refreshFunction]);

  return invalidate;
};
